import {defineStore, storeToRefs} from "pinia";
import axios from "axios";
import {ErrorDescription} from "./ErrorDescription";
import {
    BFilter,
    BFilterOptions,
    BFilterableField,
    listOperations,
    textOperations,
} from "@/components/BFilterEditor/BFilterEditor.types";
import {BSortOptions, BTableColumn, BTableOptions, serializeSortBy} from "@/components/BTable/BTable.types";
import {
    FailedFileDetails,
    FetchValidationMessagesResponse,
    Ori,
    SelectItem,
    UploadType,
    UploadedFileDetails,
} from "@/types";
import {
    defaultStatisticsColumns,
    defaultValidationMessagesColumns,
    defaultValidationMessagesFilterableFields,
} from "@/tableDefaults";
import {ref, computed} from "vue";
import {useBaseStore} from "./baseStore";
import {useOriStore} from "./oriStore";
import {serializeFilters} from "@/components/BTable/HelperFunctions";

export const useCommunityPolicingStore = defineStore("communityPolicingStore", () => {
    var baseStore = useBaseStore();
    const apiUrl = "api/v1/vacp";
    baseStore.apiUrl.value = apiUrl;
    baseStore.dataset.value = UploadType.communityPolicing;

    var oriStore = useOriStore();
    oriStore.reset();
    oriStore.dataset = UploadType.communityPolicing;

    var uploadColumns = ref([] as BTableColumn[]);

    const stopNumberOris = ref<SelectItem[]>();

    var getColumns = computed(() => {
        return [
            {
                key: "stopNumber",
                text: "Stop Number",
                sortable: true,
                value: (item) => item["stopNumber"],
            },
            {
                key: "lineNumber",
                text: "Line Number",
                sortable: true,
                value: (item) => item["lineNumber"],
            },
            {
                key: "errorCode",
                text: "Error Code",
                sortable: true,
                value: (item) => item["errorCode"],
            },
            {
                key: "errorType",
                text: "Type",
                sortable: true,
                value: (item) => item["errorType"],
            },
            {
                key: "description",
                text: "Description",
                sortable: true,
                value: (item) => item.description,
            },
        ] as BTableColumn[];
    });

    var getStatisticsColumns = computed(() => {
        return defaultStatisticsColumns;
    });

    var getFilterableFields = computed(() => {
        return [
            {
                property: "stopNumber",
                label: "Stop Number",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "errorCode",
                label: "Error Code",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: baseStore.errorCodes,
            },
            {
                property: "errorType",
                label: "Type",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: baseStore.getTypes,
            },
        ] as BFilterableField[];
    });

    var getValidationMessagesColumns = computed(() => {
        return defaultValidationMessagesColumns;
    });

    var getValidationMessagesFilterableFields = computed(() => {
        return defaultValidationMessagesFilterableFields;
    });

    var showWarningDetails = computed(() => {
        return true;
    });

    var errorCodes = computed(() => {
        return baseStore.errorCodes;
    });

    var errorDescriptions = computed(() => {
        return baseStore.errorDescriptions;
    });

    function getExportUrl(dateRange: Date[], filterInfo: BFilterOptions, filterableFields: BFilterableField[]) {
        let filterText = serializeFilters(filterInfo.filters, filterableFields);
        let dateString = "";

        dateRange.forEach((f: Date, i: Number) => {
            dateString += `,${f.getFullYear()}-${f.getMonth() + 1}-${f.getDate()}`;
        });

        return `/export-data?dateRange=${encodeURIComponent(dateString.substring(1))}&filter=${encodeURIComponent(
            filterText ?? ""
        )}`;
    }

    const getGetAvailableStopNumberYears = async function (ori: string): Promise<string[]> {
        const response = await axios.get<string[]>(`${baseStore.apiUrl.value}/${ori}/stop-number-years`);
        return response.data;
    };

    const getStopNumberOris = async function () {
        const response = await axios.get<Ori[]>(`${baseStore.apiUrl.value}/stop-number-oris`);

        stopNumberOris.value = response.data.map(
            (it) =>
                ({
                    label: it.oriAgency,
                    value: it.ori,
                } as SelectItem)
        );
    };

    function validateStopInfo(id: string, ori: string, year: number) {
        var fullUrl = baseStore.apiUrl.value;
        fullUrl += `/validate`;
        return axios.get(fullUrl, {params: {ori: ori, year: year, id: id}});
    }

    function submitDelete(id: string, ori: string, year: number) {
        var fullUrl = baseStore.apiUrl.value;
        fullUrl += `/delete`;
        return axios.delete(fullUrl, {data: {ori: ori, year: year, id: id}});
    }

    function submitZeroReport(ori: string, year: number, month: number) {
        var fullUrl = baseStore.apiUrl.value;
        fullUrl += `/zero-report`;
        return axios.post(fullUrl, {ori: ori, year: year, month: month});
    }

    function getValidationMessagesForFile(
        id: number,
        pageNumber: number,
        pageSize: number,
        filterInfo: BFilter[],
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        return baseStore.getValidationMessagesForFile(
            id,
            pageNumber,
            pageSize,
            filterInfo,
            filterableFields,
            sortingInfo
        );
    }

    function getRecordDetails(id: number) {
        return baseStore.getRecordDetails(id);
    }

    function getErrorsAndWarnings(
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        // if there's no total then were going to return the requested row count plus 1 so that we know when to disable to next page button
        return baseStore.getErrorsAndWarningsBase(`${apiUrl}/errors-warnings`, tableOptions, filters, filterableFields);
    }

    function getErrorsAndWarningsForFile(
        id: number,
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        return baseStore.getErrorsAndWarningsForUploadDetails(id, tableOptions, filters, filterableFields);
    }

    function getErrorsAndWarningsColumns(
        onlyHasOneOri: boolean,
        showAdditionalColumns: boolean,
        onlyIncludePrimaryColumns: boolean
    ) {
        let ewColumns = baseStore.getErrorsAndWarningsColumns(
            onlyHasOneOri,
            showAdditionalColumns,
            onlyIncludePrimaryColumns
        );

        var incidentNumberColumn = ewColumns.find((f) => f.key == "incidentNumber");
        if (incidentNumberColumn != undefined && ewColumns.includes(incidentNumberColumn)) {
            incidentNumberColumn.key = "stopNumber";
            incidentNumberColumn.text = "Stop Number";
        }

        let lineNumberColumn = ewColumns.find((f) => f.key == "lineNumber");
        if (lineNumberColumn != undefined && ewColumns.includes(lineNumberColumn)) {
            lineNumberColumn.columnClass = undefined;
            lineNumberColumn.class = undefined;
        }

        return ewColumns;
    }

    function getFilterableFieldsForErrorsAndWarnings(
        onlyHasOneOri: boolean,
        showAdditionalColumns: boolean,
        onlyIncludePrimaryColumns: boolean
    ) {
        var ewFilters = baseStore.getFilterableFieldsForErrorsAndWarnings(
            onlyHasOneOri,
            showAdditionalColumns,
            onlyIncludePrimaryColumns
        );

        var agencyFilter = ewFilters.find((f) => f.property == "agency");
        if (agencyFilter) {
            agencyFilter.editorOptions = oriStore.getOrisAsBFieldSelectionArray;
        }

        var incidentNumberFilter = ewFilters.find((f) => f.property == "incidentNumber");
        if (incidentNumberFilter) {
            incidentNumberFilter.property = "stopNumber";
            incidentNumberFilter.label = "Stop Number";
        }

        return ewFilters;
    }

    function getFiles(
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        // if there's no total then were going to return the requested row count plus 1 so that we know when to disable to next page button
        return baseStore.getFiles(tableOptions, filters ?? [], filterableFields);
    }
    var getFileUploadColumns = baseStore.getFileUploadColumns;
    function getFilterableFieldsForFileUploads(onlyHasOneOri: boolean, submissionPeriodHidden: boolean) {
        var filterableFields = baseStore.getFilterableFieldsForFileUploads(onlyHasOneOri, submissionPeriodHidden);

        var agencyFilter = filterableFields.find((f) => f.property == "agency");
        if (agencyFilter) {
            agencyFilter.editorOptions = oriStore.getOrisAsBFieldSelectionArray;
        }

        return filterableFields;
    }
    var errorDescriptionsDownloadUrl = ref(baseStore.errorDescriptionsDownloadUrl);

    //ori related functions & computed properties
    const {
        oris,
        getOriName,
        getOrisForSelectList,
        getOrisAsBFieldSelectionArray,
        getDetailsForCurrentDataset,
        getSelectedOri,
    } = storeToRefs(oriStore);
    var fetchDatasetDetailsForOri = oriStore.fetchDatasetDetailsForOri;
    var reset = oriStore.reset;
    var setSelectedOri = oriStore.setSelectedOri;
    var checkIfDownloadValid = baseStore.checkIfDownloadValid;

    var loadErrorsSummary = baseStore.loadErrorsSummary;
    var datasetSummary = baseStore.datasetSummary;
    var loadStatisticsSummary = baseStore.loadStatisticsSummary;
    var datasetStatisticsSummary = baseStore.datasetStatisticsSummary;
    var loadLastSubmission = baseStore.loadLastSubmission;
    var datasetLastSubmissions = baseStore.datasetLastSubmissions;

    return {
        getGetAvailableStopNumberYears,
        getColumns,
        getStatisticsColumns,
        getFilterableFields,
        getValidationMessagesColumns,
        getValidationMessagesFilterableFields,
        showWarningDetails,
        errorCodes,
        errorDescriptions,
        getExportUrl,
        getValidationMessagesForFile,
        validateStopInfo,
        submitDelete,
        submitZeroReport,
        getRecordDetails,
        getErrorsAndWarnings,
        getErrorsAndWarningsForFile,
        getFiles,
        getOriName,
        getOrisForSelectList,
        getOrisAsBFieldSelectionArray,
        getDetailsForCurrentDataset,
        fetchDatasetDetailsForOri,
        reset,
        getSelectedOri,
        setSelectedOri,
        errorDescriptionsDownloadUrl,
        getFileUploadColumns,
        getFilterableFieldsForFileUploads,
        getErrorsAndWarningsColumns,
        getFilterableFieldsForErrorsAndWarnings,
        oris,
        checkIfDownloadValid,
        loadErrorsSummary,
        datasetSummary,
        loadStatisticsSummary,
        datasetStatisticsSummary,
        loadLastSubmission,
        datasetLastSubmissions,
        apiUrl,
        getStopNumberOris,
        stopNumberOris,
    };
});
